import { IconChevronRight } from '@tabler/icons-react-native';
import { isInsideOnboardingAtom } from 'app/atoms';
import { images } from 'app/design/images';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { useTrackNewListingSource } from 'app/network/listing';
import { getSuggestedListings } from 'app/network/suggested-listing';
import { useUser } from 'app/network/user';
import { canUseOfferHub, navigateToQuickPublishFlow } from 'app/screens/intro/navigator-helper';
import { autoplayCarouselFrom } from 'app/screens/offer-hub/carousel-animation';
import { CAROUSEL_PERSPECTIVE, carouselPose } from 'app/screens/offer-hub/carousel-geometry';
import { createTheme as createDSTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import type { ImageSourcePropType, StyleProp, ViewStyle } from 'react-native';
import Animated, {
  cancelAnimation,
  useAnimatedStyle,
  useDerivedValue,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { LegacyMultipleOfferCard } from './multiple-offer-card.legacy';

const theme = createDSTheme();
const CAROUSEL_CARD_COUNT = 3;
const CAROUSEL_CENTER_X = 38;
const CAROUSEL_SPREAD = 39.25;

function useCarouselPhotoStyles(
  progress: SharedValue<number>,
  reveal: SharedValue<number>,
  index: number,
  carouselSpread?: number,
) {
  const spread = carouselSpread ?? CAROUSEL_SPREAD;
  const pose = useDerivedValue(() => carouselPose(index, progress.value, CAROUSEL_CARD_COUNT, spread));
  const layerStyle = useAnimatedStyle(() => ({ zIndex: pose.value.layer }));
  const cardStyle = useAnimatedStyle(() => {
    const { depth, rotateY, scale, translateX, translateY } = pose.value;
    return {
      shadowOpacity: 0.1 + depth * 0.18,
      shadowRadius: 6 + depth * 6,
      transform: [
        { perspective: CAROUSEL_PERSPECTIVE },
        { translateX },
        { translateY: translateY + (1 - reveal.value) * 36 },
        { rotateY: `${String(rotateY)}deg` },
        { rotate: `${String((translateX / spread) * 12)}deg` },
        { scale },
      ],
    };
  });
  const lightingStyle = useAnimatedStyle(() => ({ opacity: (1 - pose.value.depth) * 0.16 }));

  return { cardStyle, layerStyle, lightingStyle };
}

interface MultipleOfferCardProps {
  onPublish?: () => void;
  style?: StyleProp<ViewStyle>;
}

type OfferImageSources = [ImageSourcePropType, ImageSourcePropType, ImageSourcePropType];

const MOBILE_FALLBACK_IMAGES: OfferImageSources = [images.thumbnail1, images.thumbnail3, images.thumbnail2];
const DESKTOP_FALLBACK_IMAGES: OfferImageSources = [images.thumbnail1, images.thumbnail3, images.thumbnail2];

function resolveOfferImages(suggestedPictures: string[], fallbackImages: OfferImageSources): OfferImageSources {
  return fallbackImages.map((fallback, index) => {
    const uri = suggestedPictures[index];
    return uri ? { uri } : fallback;
  }) as OfferImageSources;
}

function useSuggestedOfferImages(fallbackImages: OfferImageSources): OfferImageSources {
  const { data: user } = useUser();
  const isInsideOnboarding = useAtomValue(isInsideOnboardingAtom);
  const suggestedListings = useQuery({
    queryKey: ['multiple-offer-card', 'suggested-listings', user?.id],
    queryFn: getSuggestedListings,
    enabled: Boolean(user?.id) && !isInsideOnboarding,
    refetchOnMount: 'always',
  });
  const suggestedPictures = (suggestedListings.data ?? [])
    .map((suggestion) => suggestion.listingGroup.picture)
    .filter((picture): picture is string => Boolean(picture))
    .slice(0, 3);

  return resolveOfferImages(suggestedPictures, fallbackImages);
}

export function MultipleOfferCard(props: MultipleOfferCardProps) {
  const { data: user, isPending: isUserPending } = useUser();
  const { width: viewportWidth } = useWindowDimensions();
  const showHubOfferDesign = canUseOfferHub(user?.featureFlags, isUserPending);

  if (!showHubOfferDesign) {
    return <LegacyMultipleOfferCard {...props} />;
  }

  if (viewportWidth >= 856) {
    return <DesktopHubOfferCard {...props} />;
  }

  return <HubOfferCard {...props} />;
}

function DesktopHubOfferCard(props: MultipleOfferCardProps) {
  const imageSources = useSuggestedOfferImages(DESKTOP_FALLBACK_IMAGES);

  return <LegacyMultipleOfferCard {...props} animateDesktopPhotos desktopImageSources={imageSources} />;
}

function HubOfferCard({ onPublish, style }: MultipleOfferCardProps) {
  const [bannerWidth, setBannerWidth] = useState(390);
  const navigation = useNavigation();
  const { data: user, isPending: isUserPending } = useUser();
  const imageSources = useSuggestedOfferImages(MOBILE_FALLBACK_IMAGES);
  const trackNewListingSource = useTrackNewListingSource();
  const { t } = useTranslation('landing-page');
  const reduceMotion = useReducedMotion();
  const carouselProgress = useSharedValue(0);
  const reveal = useSharedValue(reduceMotion ? 1 : 0);
  const carouselSpread = 49.25;
  const centerPhotoStyles = useCarouselPhotoStyles(carouselProgress, reveal, 0, carouselSpread);
  const rightPhotoStyles = useCarouselPhotoStyles(carouselProgress, reveal, 1, carouselSpread);
  const leftPhotoStyles = useCarouselPhotoStyles(carouselProgress, reveal, 2, carouselSpread);

  useEffect(() => {
    cancelAnimation(carouselProgress);
    cancelAnimation(reveal);
    carouselProgress.set(0);
    reveal.set(reduceMotion ? 1 : 0);

    if (reduceMotion) {
      return;
    }

    reveal.set(withTiming(1, { duration: 420 }));
    carouselProgress.set(
      withDelay(600, autoplayCarouselFrom(0, CAROUSEL_CARD_COUNT, { cardDuration: 800, roundPause: 4000 })),
    );

    return () => {
      cancelAnimation(carouselProgress);
      cancelAnimation(reveal);
    };
  }, [carouselProgress, reduceMotion, reveal]);

  const handlePress = () => {
    track(Event.MultipleOfferCardPressed);
    onPublish?.();
    trackNewListingSource('landing_page_growth_promo');
    navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending);
  };

  return (
    <Pressable
      accessibilityLabel={t('offer-hub:quickSignal', { count: 10, seconds: 20 })}
      accessibilityRole="button"
      onLayout={(event) => setBannerWidth(event.nativeEvent.layout.width)}
      onPress={handlePress}
      style={({ pressed }) => [styles.container, pressed && styles.pressed, style]}
    >
      <View style={styles.banner}>
        <IconChevronRight
          color={theme.colors['icon.primary']}
          size={24}
          strokeWidth={ICON_STROKE_WIDTH}
          style={styles.chevron}
        />
      </View>

      <View pointerEvents="none" style={[styles.cards, { transform: [{ scale: Math.min(1, bannerWidth / 390) }] }]}>
        <Animated.View collapsable={false} style={[styles.photoLayer, leftPhotoStyles.layerStyle]}>
          <Animated.View style={[styles.photoCard, leftPhotoStyles.cardStyle]}>
            <View style={styles.photoClip}>
              <PCImage contentFit="cover" source={imageSources[2]} style={StyleSheet.absoluteFill} />
              <Animated.View
                pointerEvents="none"
                style={[StyleSheet.absoluteFill, styles.lighting, leftPhotoStyles.lightingStyle]}
              />
            </View>
          </Animated.View>
        </Animated.View>

        <Animated.View collapsable={false} style={[styles.photoLayer, centerPhotoStyles.layerStyle]}>
          <Animated.View style={[styles.photoCard, centerPhotoStyles.cardStyle]}>
            <View style={styles.photoClip}>
              <PCImage contentFit="cover" source={imageSources[0]} style={StyleSheet.absoluteFill} />
              <Animated.View
                pointerEvents="none"
                style={[StyleSheet.absoluteFill, styles.lighting, centerPhotoStyles.lightingStyle]}
              />
            </View>
          </Animated.View>
        </Animated.View>

        <Animated.View collapsable={false} style={[styles.photoLayer, rightPhotoStyles.layerStyle]}>
          <Animated.View style={[styles.photoCard, rightPhotoStyles.cardStyle]}>
            <View style={styles.photoClip}>
              <PCImage contentFit="cover" source={imageSources[1]} style={StyleSheet.absoluteFill} />
              <Animated.View
                pointerEvents="none"
                style={[StyleSheet.absoluteFill, styles.lighting, rightPhotoStyles.lightingStyle]}
              />
            </View>
          </Animated.View>
        </Animated.View>
      </View>

      <View pointerEvents="none" style={styles.copy}>
        <View style={styles.badge}>
          <Text style={styles.badgeText} fontWeight="600">
            {t('multipleOfferCard.desktopBadge')}
          </Text>
        </View>
        <Text style={styles.headline} fontWeight="600">
          {t('offer-hub:quickSignal', { count: 10, seconds: 20 })}
        </Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: 120,
    position: 'relative',
    width: '100%',
    borderRadius: theme.borderRadii['radius.lg'],
    backgroundColor: '#F6DFD2',
    boxShadow: [{ offsetX: 0, offsetY: 2, blurRadius: 6, color: theme.colors['shadow.lg'] }],
    overflow: 'hidden',
  },
  pressed: { opacity: 0.72 },
  banner: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 },
  chevron: {
    position: 'absolute',
    right: 12,
    top: '50%',
    marginTop: -12,
  },
  cards: {
    height: 128,
    right: 48,
    transformOrigin: 'right bottom',
    position: 'absolute',
    bottom: -28,
    width: 156,
  },
  photoLayer: {
    height: 128,
    left: CAROUSEL_CENTER_X,
    position: 'absolute',
    top: 0,
    width: 80,
  },
  photoCard: {
    backgroundColor: theme.colors['surface.default'],
    borderColor: theme.colors['surface.default'],
    borderRadius: theme.borderRadii['radius.md'],
    borderWidth: 1,
    elevation: 3,
    flex: 1,
    shadowColor: theme.colors['shadow.lg'],
    shadowOffset: { width: 0, height: 2 },
  },
  photoClip: {
    borderRadius: Math.max(0, theme.borderRadii['radius.md'] - 1),
    flex: 1,
    overflow: 'hidden',
  },
  lighting: { backgroundColor: '#111827' },
  copy: {
    width: '50%',
    padding: 16,
    gap: 10,
  },
  badge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: theme.borderRadii['radius.sm'],
    backgroundColor: '#CD8659',
  },
  badgeText: { color: '#FFFFFF', fontSize: 12, lineHeight: 16 },
  headline: { color: '#000000', fontSize: 20, lineHeight: 24 },
});
